import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
export default function TeacherFormPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const { data: existingTeacher, isLoading: loadingTeacher } = useQuery({
        queryKey: ['teacher', id],
        queryFn: () => api.get(`/api/teachers/${id}`).then(r => r.data),
        enabled: isEdit,
    });
    const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '' });
    const [synced, setSynced] = useState(false);
    useEffect(() => {
        if (existingTeacher && !synced) {
            setFormData({
                name: existingTeacher.name || '',
                email: existingTeacher.email || '',
                phone: existingTeacher.phone || '',
                password: '',
            });
            setSynced(true);
        }
    }, [existingTeacher, synced]);
    const createMutation = useMutation({
        mutationFn: async (payload) => api.post('/api/teachers', payload),
        onSuccess: () => {
            toast.success('Teacher created');
            queryClient.invalidateQueries({ queryKey: ['teachers'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
            navigate('/teachers');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to create teacher');
        },
    });
    const updateMutation = useMutation({
        mutationFn: async (payload) => api.patch(`/api/teachers/${id}`, payload),
        onSuccess: () => {
            toast.success('Teacher updated');
            queryClient.invalidateQueries({ queryKey: ['teachers'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
            navigate('/teachers');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update teacher');
        },
    });
    const handleSubmit = (e) => {
        e.preventDefault();
        if (isEdit) {
            updateMutation.mutate(formData);
        }
        else {
            createMutation.mutate(formData);
        }
    };
    if (isEdit && loadingTeacher) {
        return (_jsx("div", { className: "flex items-center justify-center min-h-[200px]", children: _jsx(Loader2, { className: "w-6 h-6 animate-spin text-violet-500" }) }));
    }
    return (_jsxs("div", { className: "space-y-4 sm:space-y-6", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { variant: "ghost", size: "icon", className: "h-10 w-10", onClick: () => navigate(-1), children: _jsx(ArrowLeft, { className: "w-5 h-5" }) }), _jsxs("div", { children: [_jsxs("h1", { className: "text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2", children: [_jsx(Users, { className: "w-5 h-5 sm:w-6 sm:h-6 text-violet-500" }), isEdit ? 'Edit Teacher' : 'Add Teacher'] }), _jsx("p", { className: "text-xs sm:text-sm text-light-text-muted mt-1", children: isEdit ? 'Update teacher details' : 'Create a new teacher account' })] })] }), _jsx("div", { className: "glass-card p-4 sm:p-6 rounded-2xl", children: _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium mb-1", children: "Name" }), _jsx(Input, { type: "text", value: formData.name, onChange: (e) => setFormData({ ...formData, name: e.target.value }), required: true, className: "h-10" })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium mb-1", children: "Email" }), _jsx(Input, { type: "email", value: formData.email, onChange: (e) => setFormData({ ...formData, email: e.target.value }), required: true, className: "h-10" })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium mb-1", children: "Password" }), _jsx(Input, { type: "password", value: formData.password, onChange: (e) => setFormData({ ...formData, password: e.target.value }), placeholder: "Default: teacher123456", className: "h-10" })] }), _jsxs("div", { children: [_jsx(Label, { className: "text-sm font-medium mb-1", children: "Phone" }), _jsx(Input, { type: "tel", value: formData.phone, onChange: (e) => setFormData({ ...formData, phone: e.target.value }), className: "h-10" })] }), _jsxs("div", { className: "flex gap-3 pt-4", children: [_jsx(Button, { type: "button", variant: "outline", className: "flex-1 h-10", onClick: () => navigate(-1), children: "Cancel" }), _jsx(Button, { type: "submit", disabled: createMutation.isPending || updateMutation.isPending, className: "flex-1 h-10", children: createMutation.isPending || updateMutation.isPending ? (_jsx(Loader2, { className: "w-4 h-4 animate-spin" })) : (isEdit ? 'Update' : 'Create') })] })] }) })] }));
}
